import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Card, Form, Input, Button, Typography } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import { apiClient } from '../services/apiClient';
import { getApiErrorMessage } from '../utils/apiErrors';

interface ResetPasswordFormValues {
  password: string;
  confirm: string;
  token?: string;
}

export const ResetPasswordPage: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const tokenFromUrl = searchParams.get('token') || '';

  const onFinish = async (values: ResetPasswordFormValues) => {
    const token = (values.token ?? tokenFromUrl).trim();
    if (!token) {
      form.setFields([{ name: 'token', errors: ['Reset token is required'] }]);
      return;
    }
    if (values.password !== values.confirm) {
      form.setFields([{ name: 'confirm', errors: ['Passwords do not match'] }]);
      return;
    }
    setLoading(true);
    try {
      await apiClient.post('/auth/reset-password', {
        token,
        password: values.password,
      });
      navigate('/login?reset=1', { replace: true });
    } catch (err: unknown) {
      form.setFields([
        {
          name: 'password',
          errors: [getApiErrorMessage(err, 'Reset failed. Check your token or request a new link.')],
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ maxWidth: 440, margin: '48px auto', padding: 24 }}>
      <Card>
        <Typography.Title level={4} style={{ marginTop: 0 }}>
          Choose a new password
        </Typography.Title>
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          autoComplete="off"
          initialValues={{ token: tokenFromUrl }}
        >
          {!tokenFromUrl && (
            <Form.Item
              name="token"
              label="Reset token"
              rules={[{ required: true, message: 'Paste the token from your email' }]}
            >
              <Input size="large" />
            </Form.Item>
          )}
          <Form.Item
            name="password"
            label="New password"
            rules={[
              { required: true, message: 'Enter a new password' },
              { min: 8, message: 'Use at least 8 characters' },
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="New password"
              size="large"
              autoComplete="new-password"
            />
          </Form.Item>
          <Form.Item
            name="confirm"
            label="Confirm password"
            rules={[{ required: true, message: 'Confirm your password' }]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="Confirm password"
              size="large"
              autoComplete="new-password"
            />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading} block size="large">
              Update password
            </Button>
          </Form.Item>
        </Form>
        <div style={{ textAlign: 'center' }}>
          <Link to="/login">Sign in</Link>
        </div>
      </Card>
    </div>
  );
};
